import React, { useEffect, useState } from "react";
import { AlertTriangle, CandlestickChart, ChevronRight, Clock, Layers, UserRound } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Deployment, DeploymentStatus } from "../../../types/strategyDeploy";
import { ReportsMetaIconChip } from "../Reports/components/ReportsCardChrome";
import { AnimatedNumber } from "./AnimatedNumber";
import { Heartbeat } from "./Heartbeat";
import { BROKER_LABEL, GREEN, RED, STATUS_LABEL, SURF, fmtPct, signColor } from "./liveStrategiesFormat";
import { useAccountLabel } from "./useAccountLabel";
import {
  liveCardBodyClassName,
  liveCardClassName,
  liveFigureClassName,
  liveLabelClassName,
} from "./liveStrategiesUi";
import { DeployModeBadge, DeploymentStatusBadge, LiveBadge } from "./LiveUiParts";
import { MiniChart, MiniResultChart } from "./LiveChart";
import { NextStep } from "./LivePanels";
import { loadCandles } from "./SignalChart";
import type { StrategyRdOhlcBar } from "../../../types/strategyRd";

const STATUS_COLOR: Record<DeploymentStatus, string> = {
  running: GREEN,
  paused: SURF,
  stopped: "var(--muted-foreground)",
  killed: RED,
  error: RED,
};

export function DeploymentStatusDot({ status }: { status: DeploymentStatus }) {
  const c = STATUS_COLOR[status] ?? SURF;
  return (
    <span className="relative flex size-2.5 shrink-0" title={STATUS_LABEL[status]}>
      {status === "running" ? (
        <span className="absolute inline-flex size-full animate-ping rounded-full opacity-50" style={{ background: c }} />
      ) : null}
      <span className="relative inline-flex size-2.5 rounded-full" style={{ background: c }} />
    </span>
  );
}

function Metric({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className={liveLabelClassName}>{label}</span>
      <span className={cn(liveFigureClassName, "truncate")}>{children}</span>
    </div>
  );
}

/** A deployment on the board: is it alive, how it trades, and its live result vs the backtest. */
/** How much market the card's chart shows: its last 72 candles (MiniChart). */
function chartSpanLabel(candleType: string | null | undefined): string {
  const m = /^(\d+)([mhd])$/i.exec(String(candleType || "").trim());
  if (!m) return "72 candles";
  const minutes = Number(m[1]) * ({ m: 1, h: 60, d: 1440 } as Record<string, number>)[m[2].toLowerCase()] * 72;
  if (minutes < 60 * 48) return `${Math.round(minutes / 60)} hours`;
  return `${Math.round(minutes / 1440)} days`;
}

export function DeploymentCard({ dep, onOpen }: { dep: Deployment; onOpen: () => void }): JSX.Element {
  const accountName = useAccountLabel();
  const s = dep.summary ?? {};
  const closed = s.closed ?? 0;
  const winRate = closed ? (s.wins ?? 0) / closed : null;
  const expect = dep.strategy_snapshot?.backtest?.expect ?? {};
  const pending = s.pending ?? 0;
  const [candles, setCandles] = useState<StrategyRdOhlcBar[]>([]);
  useEffect(() => {
    let alive = true;
    loadCandles(dep.symbol, dep.candle_type).then((c) => alive && setCandles(c));
    return () => {
      alive = false;
    };
  }, [dep.symbol, dep.candle_type, dep.last_bar_open]);

  const account = dep.broker_name
    ? `${BROKER_LABEL[dep.broker_name] ?? dep.broker_name} ${accountName(dep.broker_name, dep.broker_account_code)}`
    : "Paper account";
  const source =
    dep.source === "pine"
      ? "TradingView"
      : dep.strategy_snapshot?.policy
        ? `Policy v${dep.strategy_snapshot.policy.version_number ?? "?"}`
        : "Strategy Lab";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      data-live-deployment-card={dep.id}
      className={cn(
        liveCardClassName,
        liveCardBodyClassName,
        "group cursor-pointer text-left outline-none transition-colors",
        "hover:!border-[color:var(--brand-tropical-surf)] focus-visible:ring-2 focus-visible:ring-[color:var(--brand-tropical-surf)]/40",
        pending > 0 && "!border-[color:var(--brand-tropical-surf)]"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="line-clamp-2 min-w-0 font-[family-name:var(--font-primary)] text-base font-medium leading-snug text-[color:var(--brand-tropical-surf)]">
          {dep.name}
        </span>
        <span className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
          <DeploymentStatusBadge status={dep.status} />
          <DeployModeBadge mode={dep.mode} />
        </span>
      </div>

      {/* Metadata chips: clicking one reveals its value instead of opening the card. */}
      <div
        className="flex flex-wrap items-center gap-1.5"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        data-live-card-meta=""
      >
        <ReportsMetaIconChip id={`symbol-${dep.id}`} label="Market" value={dep.symbol} icon={CandlestickChart} />
        <ReportsMetaIconChip id={`timeframe-${dep.id}`} label="Timeframe" value={dep.candle_type} icon={Clock} />
        <ReportsMetaIconChip id={`account-${dep.id}`} label="Account" value={account} icon={UserRound} />
        <ReportsMetaIconChip id={`source-${dep.id}`} label="Source" value={source} icon={Layers} />
      </div>

      <div className="flex flex-col gap-2">
        {/* Once it has closed trades: its running result. Before that: the market it watches. */}
        {(s.r_path?.length ?? 0) > 0 ? (
          <>
            <span className={liveLabelClassName}>
              Result: {s.r_path!.length} closed trade{s.r_path!.length === 1 ? "" : "s"} (running R)
            </span>
            <MiniResultChart rPath={s.r_path!} />
          </>
        ) : (
          <>
            <span className={liveLabelClassName}>
              {dep.symbol} price, last {chartSpanLabel(dep.candle_type)}
            </span>
            <MiniChart candles={candles} />
          </>
        )}
        <NextStep next={dep.next} compact />
      </div>

      <div className="grid grid-cols-4 gap-3 !border-0 !border-t !border-solid !border-border pt-4">
        <Metric label="P&L">
          <AnimatedNumber value={s.pnl ?? 0} decimals={2} prefix="$" signed style={{ color: signColor(s.pnl) }} />
        </Metric>
        <Metric label="Total R">
          <AnimatedNumber value={s.r_total ?? 0} decimals={2} suffix="R" signed style={{ color: signColor(s.r_total) }} />
        </Metric>
        <Metric label="Won">
          {winRate != null ? fmtPct(winRate) : "N/A"}
          {expect.win_rate != null ? (
            <span className="text-[11px] font-normal text-gray-500 dark:text-white" title="Backtest win rate">
              {" "}
              vs {fmtPct(expect.win_rate)}
            </span>
          ) : null}
        </Metric>
        <Metric label="Trades">
          {closed}
          {(s.open_trades ?? 0) > 0 ? <span style={{ color: SURF }}> +{s.open_trades} open</span> : null}
        </Metric>
      </div>

      <div className="flex items-center justify-between gap-3 !border-0 !border-t !border-solid !border-border pt-4">
        {dep.last_error && dep.status === "running" ? (
          <span className="flex min-w-0 items-center gap-1.5 truncate text-xs" style={{ color: RED }} title={dep.last_error}>
            <AlertTriangle className="size-3.5 shrink-0" aria-hidden /> {dep.last_error}
          </span>
        ) : (
          <Heartbeat dep={dep} className="text-gray-500 dark:text-white" />
        )}
        <span className="flex shrink-0 items-center gap-2">
          {pending > 0 ? (
            <LiveBadge tone="brand" pulse>
              {pending} to approve
            </LiveBadge>
          ) : null}
          <Button
            type="button"
            size="sm"
            variant="outline-brand"
            data-font="primary"
            className="h-8 gap-1 px-3 shadow-none"
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
            data-live-deployment-open=""
          >
            <span className="normal-case tracking-normal">Open</span>
            <ChevronRight className="size-3.5" aria-hidden />
          </Button>
        </span>
      </div>
    </div>
  );
}
